import React from "react";
import { Button, Col, DatePicker, Flex, Form, Input, notification, Row, Space, Spin, Table, Tooltip, Upload } from "antd";
import Icon from "@mdi/react";
import { mdiDownload, mdiUpload } from "@mdi/js";
import { useParams } from "react-router-dom";
import B2C2BApi from "../../api-services/b2c2b-api.service";
import { GroundTourBookingOrder } from "../../interfaces/booking.interface";
import { b2c2bDateFormat, b2c2bUploadProps, bookingFormValidationMessages } from "../../utils/antd.props";

const getFile = event => event.file;
const fallback = value => value || 'TBA';

function withParams(Component) {
  return props => <Component {...props} params={useParams()} />;
}

class GroundTourOrderInformationPage extends React.Component<{ params }> {
  state: { loading: boolean; submitting: boolean; order: GroundTourBookingOrder | null } = {
    loading: true,
    submitting: false,
    order: null,
  };

  componentDidMount() {
    this.loadBooking();
  }

  loadBooking = () => {
    this.setState({ loading: true, order: null });
    B2C2BApi.viewLandTourBooking(this.props.params.id)
      .then(order => this.setState({ order }))
      .catch(error => notification.error({ message: error.response?.data?.error || 'Unable to load booking.' }))
      .finally(() => this.setState({ loading: false }));
  };

  submit = (values) => {
    this.setState({ submitting: true });
    B2C2BApi.updateLandTourBooking(this.props.params.id, values)
      .then(() => {
        notification.success({ message: 'Booking updated successfully' });
        this.loadBooking();
      })
      .catch(error => notification.error({ message: error.response?.data?.error || 'Unable to update booking.' }))
      .finally(() => this.setState({ submitting: false }));
  };

  detailCell(label, value) {
    return (
      <Col xs={24} sm={12}>
        <div className="bookings-page_table-grid-cell">
          <span className="label">{label}</span><span>{value || '-'}</span>
        </div>
      </Col>
    );
  }

  flightDetails(order: GroundTourBookingOrder) {
    const disabled = order.incFlight || !order.editable;
    return (
      <div className="bookings-page_table b2c2b-section_panel">
        <label>
          Flight Details <span className="note">{order.incFlight ? '*Included flight schedule' : '*Add your flight details if available'}</span>
        </label>
        <Row gutter={[20, 0]}>
          <Col xs={24} md={12}><Form.Item name={['flights', 'departureDate']} label="Departure Date"><DatePicker disabled={disabled} format={b2c2bDateFormat} style={{ width: '100%' }} /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name={['flights', 'originDestination']} label="Departure From / To"><Input disabled={disabled} /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name={['flights', 'flightNo']} label="Departure Flight No."><Input disabled={disabled} /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name={['flights', 'arrivalTime']} label="Departure / Arrival Time"><Input disabled={disabled} /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name={['flights', 'returnDate']} label="Return Date"><DatePicker disabled={disabled} format={b2c2bDateFormat} style={{ width: '100%' }} /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name={['flights', 'returnOriginDestination']} label="Return From / To"><Input disabled={disabled} /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name={['flights', 'returnFlightNo']} label="Return Flight No."><Input disabled={disabled} /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name={['flights', 'returnArrivalTime']} label="Return / Arrival Time"><Input disabled={disabled} /></Form.Item></Col>
        </Row>
      </div>
    );
  }

  guestDetails(order: GroundTourBookingOrder) {
    return (
      <Form.List name="guests">
        {fields => (
          <Space size={20} direction="vertical" style={{ display: 'flex' }}>
            {fields.map((field, index) => {
              const guest = order.guests[index];
              const disabled = !order.editable;
              const passportRequired = !guest.passport_photocopy_image;
              const wechatRequired = index === 0 && order.wechatRequired && !guest.wechat_qrcode_image;
              return (
                <div key={field.key} className="b2c2b-section_panel">
                  <div className="b2c2b-section_panel-header"><label>Guest {index + 1}</label></div>
                  <div className="b2c2b-section_panel-form">
                    <Form.Item name={[field.name, 'id']} hidden><Input /></Form.Item>
                    <Row gutter={[20, 0]}>
                      <Col xs={24} md={12}><Form.Item name={[field.name, 'designation']} label="Designation"><Input disabled={disabled} /></Form.Item></Col>
                      <Col xs={24} md={12}><Form.Item name={[field.name, 'category']} label="Room Category"><Input disabled /></Form.Item></Col>
                      <Col xs={24} md={12}><Form.Item name={[field.name, 'surname']} label="Surname on Passport"><Input disabled={disabled} /></Form.Item></Col>
                      <Col xs={24} md={12}><Form.Item name={[field.name, 'given_name']} label="Given Name on Passport"><Input disabled={disabled} /></Form.Item></Col>
                      <Col xs={24} md={12}><Form.Item name={[field.name, 'date_of_birth']} label="Date of Birth"><DatePicker disabled={disabled} format={b2c2bDateFormat} style={{ width: '100%' }} /></Form.Item></Col>
                      <Col xs={24} md={12}><Form.Item name={[field.name, 'mobile']} label="Mobile Number"><Input disabled={disabled} /></Form.Item></Col>
                      <Col xs={24} md={12}><Form.Item name={[field.name, 'nationality']} label="Nationality"><Input disabled={disabled} /></Form.Item></Col>
                      <Col xs={24} md={12}><Form.Item name={[field.name, 'passport_number']} label="Passport Number" rules={[{ required: true }]}><Input disabled={disabled} /></Form.Item></Col>
                      <Col xs={24} md={12}>
                        <Form.Item name={[field.name, 'as_passport_photocopy']} label="Passport Photocopy"
                          getValueFromEvent={getFile} valuePropName="file" rules={[{ required: passportRequired }]}>
                          <Upload {...b2c2bUploadProps} maxCount={1} disabled={disabled}>
                            <Input disabled={disabled} prefix={<Icon path={mdiUpload} size="19px" />} placeholder="Upload passport photocopy" />
                          </Upload>
                        </Form.Item>
                        {guest.passport_photocopy_image && <a href={guest.passport_photocopy_image} target="_blank" rel="noreferrer">View passport photocopy</a>}
                      </Col>
                      {index === 0 && order.wechatRequired && (
                        <Col xs={24} md={12}>
                          <Form.Item name={[field.name, 'wechat_qrcode']} label="WeChat QR Code"
                            getValueFromEvent={getFile} valuePropName="file" rules={[{ required: wechatRequired }]}>
                            <Upload {...b2c2bUploadProps} maxCount={1} disabled={disabled}>
                              <Input disabled={disabled} prefix={<Icon path={mdiUpload} size="19px" />} placeholder="Upload WeChat QR code" />
                            </Upload>
                          </Form.Item>
                          {guest.wechat_qrcode_image && <a href={guest.wechat_qrcode_image} target="_blank" rel="noreferrer">View WeChat QR code</a>}
                        </Col>
                      )}
                      <Col span={24}><Form.Item name={[field.name, 'customer_remark']} label="Remark"><Input.TextArea disabled={disabled} /></Form.Item></Col>
                    </Row>
                  </div>
                </div>
              );
            })}
          </Space>
        )}
      </Form.List>
    );
  }

  voucherAction(order: GroundTourBookingOrder) {
    if (!order.voucherUrl) return <span>Voucher becomes available 7 days before departure.</span>;
    const downloadable = order.voucherDownloadable === 'Downloadable';
    const reasons = order.voucherDownloadable?.split('\n').map(reason => <div key={reason}>{reason}</div>);
    return (
      <Tooltip title={downloadable ? undefined : reasons}>
        <Button type="primary" disabled={!downloadable} icon={<Icon path={mdiDownload} size="18px" />}>
          <a href={order.voucherUrl} target="_blank" rel="noreferrer">Download Voucher</a>
        </Button>
      </Tooltip>
    );
  }

  render() {
    const order = this.state.order;
    if (this.state.loading && !order) return <div className="bookings-page"><Spin /></div>;
    if (!order) return null;

    return (
      <div id="ground-order-information" className="bookings-page b2c2b-section">
        {this.state.submitting && <Spin fullscreen />}
        <div className="bookings-page_header"><h1>Ground Tour Order Information</h1></div>
        <Form layout="vertical" variant="filled" initialValues={order} onFinish={this.submit}
          validateMessages={bookingFormValidationMessages} scrollToFirstError>
          <Space size={40} direction="vertical" style={{ display: 'flex' }}>
            <div className="bookings-page_table b2c2b-section_panel">
              <label>Order Details</label>
              <Row>
                {this.detailCell('Ref No.', order.booking.refNo)}
                {this.detailCell('Tour Code', order.booking.tourCode)}
                {this.detailCell('Destination', order.booking.destination)}
                {this.detailCell('Departure Date', order.booking.departureDate)}
                {this.detailCell('Package Name', order.booking.packageName)}
                {this.detailCell('Status', order.booking.status)}
                {this.detailCell('Total Price', order.booking.totalPrice)}
                {this.detailCell('Paid Amount', order.booking.paidAmount)}
                {this.detailCell('Balance', order.booking.balanceAmount)}
                {this.detailCell('Agent', order.agent?.name)}
              </Row>
            </div>

            <div className="bookings-page_table b2c2b-section_panel">
              <label>Hotel</label>
              {order.hotels.length ? order.hotels.map(hotel => <div key={hotel}>{hotel || 'TBA'}</div>) : <div>TBA</div>}
            </div>

            <div className="bookings-page_table b2c2b-section_panel">
              <label>Restaurant</label>
              {order.restaurants.length ? (
                <Table dataSource={order.restaurants.map(row => ({ ...row, key: row.day_no }))} pagination={false} scroll={{ x: 'max-content' }}>
                  <Table.Column title="Day" dataIndex="day_no" render={value => `Day ${value}`} />
                  <Table.Column title="Breakfast" dataIndex="breakfast" render={fallback} />
                  <Table.Column title="Lunch" dataIndex="lunch" render={fallback} />
                  <Table.Column title="Dinner" dataIndex="dinner" render={fallback} />
                </Table>
              ) : <div>TBA</div>}
            </div>

            {this.flightDetails(order)}
            <div id="passenger-info">{this.guestDetails(order)}</div>

            <Flex justify="center" align="center" gap={12} wrap>
              {order.editable && <Button type="primary" htmlType="submit">Update</Button>}
              {this.voucherAction(order)}
            </Flex>
          </Space>
        </Form>
      </div>
    );
  }
}

export default withParams(GroundTourOrderInformationPage);
